import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { CANCELED_BY_USER } from "@modules/common/types/errorTypes"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useStripePayment } from "@modules/finances/hooks/useStripeAccount"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoggerType } from "@modules/utils/loggerUtils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { useState } from "react"

export const useBoxOfferSubscription = () => {
  const [isProcessing, setIsProcessing] = useState(false)

  const { boxOfferApi } = useServicesContext()
  const { stripePayment } = useStripePayment()

  const subscribeToFreeOffer = useMutation({
    mutationFn: async ({ boxId, offerId }: { boxId: string; offerId: string }) => {
      return await boxOfferApi.subscribeFree(boxId, offerId)
    },
  })

  const subscribeToPayingOffer = useMutation({
    mutationFn: async ({ offerId }: { offerId: string }) => {
      const currentUrl = window.location.href
      return await boxOfferApi.createBoxOfferPaymentSubscription(offerId, currentUrl, currentUrl)
    },
    onSuccess: async (data) => {
      try {
        setIsProcessing(true)
        const success = await stripePayment(data.url)
        if (success) {
          toast.success(word("program.subscribe.success.toast"), {
            classNames: successToastClassName(),
          })
        }
      } catch (e) {
        if (e !== CANCELED_BY_USER) {
          if ((e as AxiosError).code === "PROGRAM_HAS_NO_PRICE") {
            console.warn(LoggerType.Program + "try to paid a free offer ...")
          } else {
            console.error(LoggerType.Stripe + "subscribe on paid offer", e)
            toast.error(word("program.subscribe.error.toast"), {
              classNames: errorToastClassName(),
            })
          }
        }
        return false
      } finally {
        setIsProcessing(false)
      }
    },
    onError: () => {
      toast.error(word("program.subscribe.error.toast"), {
        classNames: errorToastClassName(),
      })
    },
  })

  const subscribeToOffer = (boxId: string, offerId: string, isFree: boolean) => {
    if (isFree) {
      return subscribeToFreeOffer.mutateAsync({ boxId, offerId })
    } else {
      return subscribeToPayingOffer.mutateAsync({ offerId })
    }
  }

  return {
    subscribeToOffer,
    isProcessing: isProcessing || subscribeToFreeOffer.isPending || subscribeToPayingOffer.isPending,
  }
}

export const useBoxOfferInvitationByUser = () => {
  const { boxOfferApi } = useServicesContext()
  const { hideSidebar } = useSidebar()
  const queryClient = useQueryClient()

  const refuseOfferInvitation = useMutation({
    mutationFn: async ({ boxOfferInvitationId }: { boxOfferInvitationId: string }) => {
      return await boxOfferApi.refuseOfferInvitation(boxOfferInvitationId)
    },
    onSuccess: (data) => {
      toast.success(word("box.offer.user.toast.refuseOfferInvitation.success"), {
        classNames: successToastClassName(),
      })

      queryClient.invalidateQueries({
        queryKey: ["boxOfferUser", data.boxOfferId, data.boxId],
      })

      hideSidebar()
    },
    onError: () => {
      toast.error(word("box.offer.user.toast.refuseOfferInvitation.error"), {
        classNames: errorToastClassName(),
      })
    },
  })

  return {
    refuseOfferInvitation,
  }
}
